import {
  RecurringTransactionType,
  TransactionAmountType,
  type TransactionFilters,
} from '../../__generated__/globalTypes';

import type { TransactionStats } from '../../hooks/queries/useTransactionStats';

export type TransactionStatsScope =
  | { kind: 'merchant'; merchantId: string }
  | { kind: 'recurringTransaction'; recurringTransactionId: string; streamType: RecurringTransactionType };

/**
 * All-time filters behind a record page's spending numbers: one sign, no date
 * window. An INCOME stream's payments are credits, so its sign flips.
 */
export function buildTransactionStatsFilters(scope: TransactionStatsScope): TransactionFilters {
  if (scope.kind === 'merchant') {
    return { merchantIds: [scope.merchantId], transactionAmountType: TransactionAmountType.Debit };
  }
  return {
    recurringTransactionId: scope.recurringTransactionId,
    transactionAmountType:
      scope.streamType === RecurringTransactionType.Income ? TransactionAmountType.Credit : TransactionAmountType.Debit,
  };
}

export interface IAmountRangeBarValues {
  min: number;
  max: number;
  median: number;
}

/**
 * An `AmountRangeBar`'s scale from a stats row. The stats keep the sign of the
 * set they total, so a credit-side set (an income stream) comes back negative
 * with `min` the largest payment; the bar wants magnitudes, smallest first.
 */
export function toAmountRangeBarValues(stats: Pick<TransactionStats, 'min' | 'max' | 'median'>): IAmountRangeBarValues {
  const ends = [Math.abs(Number(stats.min)), Math.abs(Number(stats.max))];
  return { min: Math.min(...ends), max: Math.max(...ends), median: Math.abs(Number(stats.median)) };
}

/**
 * A record's amount as the bar's current marker, or null when it sits on the
 * wrong side of the stream — a refund on a bill, a reversal on income — since
 * those are not in the set the stats describe. Debits are positive as the API
 * serializes them; an income stream's payments are the negative credits.
 */
export function currentAmountForStream(
  amount: string | null | undefined,
  streamType: RecurringTransactionType
): number | null {
  if (amount == null) {
    return null;
  }
  const value = Number(amount);
  if (!Number.isFinite(value) || value === 0) {
    return null;
  }
  const isCredit = value < 0;
  const expectsCredit = streamType === RecurringTransactionType.Income;
  return isCredit === expectsCredit ? Math.abs(value) : null;
}
